import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
  Modal,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxes,
  faCheck,
  faSave,
  faUndo,
  faInfoCircle,
  faBuilding,
  faCapsules,
  faLayerGroup,
  faList,
  faEdit,
  faSync,
  faCheckCircle,
  faExclamationTriangle,
  faChevronLeft
} from "@fortawesome/free-solid-svg-icons";
import axios from "helpers/api";
import { toast } from "react-toastify";
import SubtleBadge from "components/common/SubtleBadge";
import ScheduleSymbol from "components/inventory/ScheduleSymbol";
import ScheduleBadge, { SCHEDULE_COLORS } from "components/inventory/ScheduleBadge";
import AuditSettings, { validateAuditSettings } from "components/inventory/AuditSettings";
import { readAuditConfig, auditConfigPayload } from "components/inventory/auditApi";
import useScheduleTokens, {
  fetchScheduleTokens,
  shortLabelFor
} from "components/inventory/scheduleTokens";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";

const SCHEDULE_OPTIONS = [
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
  { value: "-999", label: "Non Controlled" },
  { value: "otc", label: "OTC" }
];

const SCHEDULE_LABEL_MAP = {
  "2": "2",
  "3": "3",
  "4": "4",
  "5": "5",
  "-999": "Non Controlled",
  "otc": "OTC"
};

// Presentation-only metadata used to make the schedule tiles self-explanatory.
const SCHEDULE_META = {
  "2": { title: "C 2", hint: "Controlled" },
  "3": { title: "C 3", hint: "Controlled" },
  "4": { title: "C 4", hint: "Controlled" },
  "5": { title: "C 5", hint: "Controlled" },
  "-999": { title: "Non Controlled", hint: "Regular prescription drugs" },
  "otc": { title: "OTC", hint: "Over the counter" }
};

// Accent colour per schedule, taken from SCHEDULE_COLORS so these cards always
// match the schedule badges. The symbol itself is drawn by ScheduleSymbol from
// the token, so C-II ... C-V render as the DEA controlled-substance mark.
const SCHEDULE_STYLE = Object.fromEntries(
  Object.entries(SCHEDULE_COLORS).map(([token, color]) => [token, { color }])
);

const DEFAULT_TILE_STYLE = { color: "primary" };

// Bootstrap columns follow the browser width, not the container, so a narrow
// container (the facility modal) asks for fewer cards per row.
const DEFAULT_TILE_COLS = { xs: 12, sm: 6, lg: 3 };
const COMPACT_TILE_COLS = { xs: 12, sm: 6, lg: 4 };
const PRN_TILE_STYLE = { color: SCHEDULE_COLORS.prn, token: "prn" };

const SCHEDULE_ORDER_MAP = {
  "2": 1,
  "3": 2,
  "4": 3,
  "5": 4,
  "otc": 5,
  "-999": 6
};

let VALID_SCHEDULE_VALUES = SCHEDULE_OPTIONS.map((o) => o.value.toLowerCase());

// Accept any token the API reports, on top of the built-in fallback list.
fetchScheduleTokens().then((list) => {
  VALID_SCHEDULE_VALUES = Array.from(
    new Set([...VALID_SCHEDULE_VALUES, ...list.map((t) => String(t.token).toLowerCase())])
  );
});

export const getSortedSchedules = (schedules = []) => {
  return [...schedules]
    .filter((s) => VALID_SCHEDULE_VALUES.includes(String(s).toLowerCase()))
    .sort((a, b) => {
      const orderA = SCHEDULE_ORDER_MAP[a] !== undefined ? SCHEDULE_ORDER_MAP[a] : 99;
      const orderB = SCHEDULE_ORDER_MAP[b] !== undefined ? SCHEDULE_ORDER_MAP[b] : 99;
      return orderA - orderB;
    });
};

const parseSchedules = (schedulesStrOrList, schedulesListArray) => {
  let list = [];
  if (Array.isArray(schedulesListArray) && schedulesListArray.length > 0) {
    list = schedulesListArray.map(String).map((s) => s.trim().toLowerCase());
  } else if (typeof schedulesStrOrList === "string" && schedulesStrOrList.trim()) {
    list = schedulesStrOrList.split(",").map((s) => s.trim().toLowerCase());
  } else if (Array.isArray(schedulesStrOrList) && schedulesStrOrList.length > 0) {
    list = schedulesStrOrList.map(String).map((s) => s.trim().toLowerCase());
  }
  return list.filter((s) => VALID_SCHEDULE_VALUES.includes(s));
};

const getGroupId = () =>
  localStorage.getItem("groupId") || localStorage.getItem("group_id") || "61175";

const authHeaders = () => {
  const tokenStr = localStorage.getItem("accessToken");
  return tokenStr ? { Authorization: `Bearer ${tokenStr}` } : {};
};

const apiUrl = (path) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  return baseUrl ? `${baseUrl}${path}` : path;
};

/* ------------------------------------------------------------------ */
/* Shared presentational helpers                                       */
/* ------------------------------------------------------------------ */

const ScheduleTile = ({
  option,
  isChecked,
  onToggle,
  idPrefix,
  fallbackHint,
  title,
  hint,
  tileStyle,
  colProps = DEFAULT_TILE_COLS
}) => {
  const meta = SCHEDULE_META[option.value] || {
    title: shortLabelFor(option.value),
    hint: fallbackHint || option.label
  };
  const tileTitle = title || meta.title || option.label;
  const tileHint = hint || meta.hint;
  const { color, token } = tileStyle || SCHEDULE_STYLE[option.value] || DEFAULT_TILE_STYLE;
  const symbolToken = token || option.value;

  return (
    <Col {...colProps}>
      <div
        role="checkbox"
        aria-checked={isChecked}
        aria-label={tileTitle}
        tabIndex={0}
        onClick={() => onToggle(option.value)}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onToggle(option.value);
          }
        }}
        className={`position-relative h-100 d-flex align-items-center gap-3 p-3 pe-4 rounded-3 border text-start ${
          isChecked ? `border-${color} bg-${color}-subtle` : "border-200 bg-body-tertiary"
        }`}
        style={{
          cursor: "pointer",
          transition: "all .15s ease-in-out",
          borderWidth: isChecked ? 2 : 1,
          boxShadow: isChecked ? "0 2px 8px rgba(0, 0, 0, .07)" : "none"
        }}
      >
        <span
          className={`position-absolute top-0 end-0 mt-2 me-2 d-inline-flex align-items-center justify-content-center rounded-circle ${
            isChecked ? `bg-${color} text-white` : "border border-300"
          }`}
          style={{ width: 18, height: 18 }}
        >
          {isChecked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />}
        </span>

        <Form.Check
          type="checkbox"
          id={`${idPrefix}-${option.value}`}
          checked={isChecked}
          onChange={() => {}}
          className="d-none"
        />

        <span
          className={`d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
            isChecked ? `bg-${color} text-white` : "bg-body border border-300 text-500"
          }`}
          style={{ width: 44, height: 44, transition: "all .15s ease-in-out" }}
        >
          <ScheduleSymbol token={symbolToken} size={28} title={tileTitle} />
        </span>

        <span className="flex-1 text-break" style={{ minWidth: 0 }}>
          <span className={`d-block fw-bold fs-9 lh-sm mb-1 ${isChecked ? `text-${color}` : "text-900"}`}>
            {tileTitle}
          </span>
          <span className="d-block text-600 fs-10 lh-sm">{tileHint}</span>
        </span>
      </div>
    </Col>
  );
};

ScheduleTile.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }).isRequired,
  isChecked: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  idPrefix: PropTypes.string,
  fallbackHint: PropTypes.string,
  title: PropTypes.string,
  hint: PropTypes.string,
  tileStyle: PropTypes.object,
  colProps: PropTypes.object
};

/* Small section heading used at the top of the inventory tabs. */
const SectionHeading = ({ title, subtitle }) => (
  <div className="mb-3">
    <h6 className="mb-1 fw-bold text-900 fs-9">
      <FontAwesomeIcon icon={faBoxes} className="text-primary me-2" />
      {title}
    </h6>
    {subtitle && <p className="text-600 mb-0 fs-10">{subtitle}</p>}
  </div>
);

SectionHeading.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string
};

// Shortcuts above the schedule cards, styled as small Falcon buttons so they
// stand out from the description text.
const ScheduleQuickActions = ({ onAll, onControlled, onClear }) => (
  <div className="d-flex flex-wrap gap-2">
    <Button variant="falcon-primary" size="sm" className="fw-semibold px-3" onClick={onAll}>
      Select all
    </Button>
    <Button variant="falcon-primary" size="sm" className="fw-semibold px-3" onClick={onControlled}>
      Controlled only (2–5)
    </Button>
    <Button variant="falcon-default" size="sm" className="fw-semibold px-3" onClick={onClear}>
      Clear
    </Button>
  </div>
);

ScheduleQuickActions.propTypes = {
  onAll: PropTypes.func.isRequired,
  onControlled: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired
};

const ScheduleChips = ({ schedules, center, labelOf }) => {
  if (!schedules || schedules.length === 0) {
    return <span className="text-500 fst-italic fs-10">Not set</span>;
  }
  return (
    <div className={`d-flex flex-wrap gap-1 ${center ? "justify-content-center" : ""}`}>
      {getSortedSchedules(schedules).map((s) => (
        <ScheduleBadge key={s} token={s} title={labelOf ? labelOf(s) : undefined} />
      ))}
    </div>
  );
};

ScheduleChips.propTypes = {
  schedules: PropTypes.array,
  center: PropTypes.bool,
  labelOf: PropTypes.func
};

const ScheduleGrid = ({
  idPrefix,
  selected,
  onToggle,
  onAll,
  onControlled,
  onClear,
  title,
  subtitle,
  prn,
  compact = false
}) => {
  const { tokens, labelOf } = useScheduleTokens();
  const colProps = compact ? COMPACT_TILE_COLS : DEFAULT_TILE_COLS;

  return (
  <>
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div>
        <h6 className="mb-1 fw-bold text-900 fs-10">
          <FontAwesomeIcon icon={faCapsules} className="text-primary me-2" />
          {title}
        </h6>
        <p className="text-600 mb-0 fs-10">{subtitle}</p>
      </div>
      <ScheduleQuickActions onAll={onAll} onControlled={onControlled} onClear={onClear} />
    </div>

    <Row className="g-3">
      {tokens.map((t) => {
        const value = String(t.token);
        return (
          <ScheduleTile
            key={value}
            option={{ value, label: t.label }}
            fallbackHint={t.label}
            idPrefix={idPrefix}
            isChecked={selected.includes(value.toLowerCase())}
            onToggle={onToggle}
            colProps={colProps}
          />
        );
      })}

      {prn && (
        <ScheduleTile
          option={{ value: "all-prn", label: "All PRN" }}
          idPrefix={idPrefix}
          title="All PRN"
          hint="Track every as-needed order"
          tileStyle={PRN_TILE_STYLE}
          isChecked={Boolean(prn.checked)}
          onToggle={() => prn.onToggle(!prn.checked)}
          colProps={colProps}
        />
      )}
    </Row>

    <div className="d-flex flex-wrap align-items-center gap-2 mt-4 pt-3 border-top border-200">
      <span className="fw-semibold fs-10 text-800">Selected ({selected.length}):</span>
      <ScheduleChips schedules={selected} labelOf={labelOf} />
      {prn?.checked && <ScheduleBadge token="prn" title="All PRN" />}
    </div>
  </>
  );
};

ScheduleGrid.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onAll: PropTypes.func.isRequired,
  onControlled: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  prn: PropTypes.shape({
    checked: PropTypes.bool,
    onToggle: PropTypes.func
  }),
  compact: PropTypes.bool
};

const SaveBar = ({ dirty, saving, onSave, onReset, saveLabel, sticky }) => (
  <div
    className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3 rounded-3 border border-200 bg-body-tertiary"
    style={sticky ? { position: "sticky", bottom: 0, zIndex: 5 } : undefined}
  >
    <div className="fs-10">
      {dirty ? (
        <span className="text-warning fw-semibold">
          <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
          You have unsaved changes
        </span>
      ) : (
        <span className="text-600">
          <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
          All changes saved
        </span>
      )}
    </div>
    <div className="d-flex gap-2">
      {onReset && (
        <Button
          variant="outline-secondary"
          className="px-3 fw-semibold rounded-3"
          onClick={onReset}
          disabled={!dirty || saving}
        >
          <FontAwesomeIcon icon={faUndo} className="me-1" />
          Reset
        </Button>
      )}
      <Button
        variant="primary"
        className="px-4 fw-semibold rounded-3"
        onClick={onSave}
        disabled={saving}
      >
        {saving ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Saving...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faSave} className="me-1" />
            {saveLabel}
          </>
        )}
      </Button>
    </div>
  </div>
);

SaveBar.propTypes = {
  dirty: PropTypes.bool,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  saveLabel: PropTypes.string,
  sticky: PropTypes.bool
};

/* ------------------------------------------------------------------ */
/* Shared data hook: group config + facilities of the group            */
/* ------------------------------------------------------------------ */

const useGroupInventory = (groupIdOverride) => {
  const groupId = groupIdOverride || getGroupId();
  const [groupConfig, setGroupConfig] = useState(null);
  const [groupFacilities, setGroupFacilities] = useState([]);
  const [loadingGroup, setLoadingGroup] = useState(false);

  const fetchGroupData = useCallback(async (gId) => {
    if (!gId) return;
    setLoadingGroup(true);
    const headers = authHeaders();

    try {
      // 1. Group config: GET /inventory/config/group/:groupId
      try {
        const groupRes = await axios.get(apiUrl(`/inventory/config/group/${gId}`), { headers });
        const gData = groupRes.data?.data || groupRes.data?.config || groupRes.data;
        if (gData) setGroupConfig(gData);
      } catch (err) {
        console.warn("Group config fetch warning:", err?.message);
      }

      // 2. Facilities of the group: GET /inventory/config?groupId=:groupId
      const facRes = await axios.get(apiUrl(`/inventory/config?groupId=${gId}`), { headers });
      const facList = facRes.data?.data || [];
      if (Array.isArray(facList)) setGroupFacilities(facList);
    } catch (err) {
      console.error("Error fetching group inventory configurations:", err);
    } finally {
      setLoadingGroup(false);
    }
  }, []);

  useEffect(() => {
    fetchGroupData(groupId);
  }, [fetchGroupData, groupId]);

  return {
    groupId,
    groupConfig,
    groupFacilities,
    setGroupFacilities,
    loadingGroup,
    refresh: () => fetchGroupData(groupId)
  };
};

/* ------------------------------------------------------------------ */
/* Facility list (Falcon advance table + search box)                   */
/* ------------------------------------------------------------------ */

const FacilityList = ({ facilities, loading, onSelect, onRefresh }) => {
  const rows = useMemo(
    () =>
      facilities.map((fac) => {
        const schedules = parseSchedules(fac.inventory_schedules, fac.inventory_schedules_list);
        return {
          facility_name: fac.facility_name || "Facility",
          inventory_status: fac.inventory_enabled ? "Enabled" : "Disabled",
          schedules_label:
            schedules.length > 0
              ? schedules.map((s) => SCHEDULE_LABEL_MAP[s] || s).join(", ")
              : "Not set",
          settings_source: fac.inherits_from_group ? "Group default" : "Facility custom",
          schedules,
          raw: fac
        };
      }),
    [facilities]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "facility_name",
        header: "Facility Name",
        meta: {
          headerProps: { className: "pe-1 text-900 text-start" }
        },
        cell: ({ row: { original } }) => (
          <Button
            variant="link"
            className="p-0 fw-semibold text-decoration-none"
            onClick={() => onSelect(original.raw)}
          >
            {original.facility_name}
          </Button>
        )
      },
      {
        accessorKey: "inventory_status",
        header: "Inventory Status",
        meta: {
          headerProps: { className: "pe-1 text-900 text-start" }
        },
        cell: ({ row: { original } }) => (
          <SubtleBadge
            bg={original.raw.inventory_enabled ? "success" : "secondary"}
            pill
            className="d-inline-block"
          >
            <FontAwesomeIcon
              icon={original.raw.inventory_enabled ? faCheckCircle : faInfoCircle}
              className="me-1"
            />
            {original.inventory_status}
          </SubtleBadge>
        )
      },
      {
        accessorKey: "schedules_label",
        header: "Tracked Schedules",
        meta: {
          headerProps: { className: "pe-1 text-900 text-start" }
        },
        cell: ({ row: { original } }) => <ScheduleChips schedules={original.schedules} />
      },
      {
        accessorKey: "settings_source",
        header: "Configuration Mode",
        meta: {
          headerProps: { className: "pe-1 text-900 text-start" }
        },
        cell: ({ row: { original } }) => (
          <SubtleBadge
            bg={original.raw.inherits_from_group ? "warning" : "primary"}
            pill
            className="d-inline-block"
          >
            {original.settings_source}
          </SubtleBadge>
        )
      }
    ],
    [onSelect]
  );

  const table = useAdvanceTable({
    data: rows,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  return (
    <AdvanceTableProvider {...table}>
      <Row className="g-2 align-items-end mb-3">
        <Col xs={12} md="auto" className="d-flex gap-2 me-md-auto" style={{ minWidth: 260 }}>
          <AdvanceTableSearchBox placeholder="Search facilities..." />
          {/* <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
            <Button
              variant="outline-secondary"
              size="sm"
              className="border-300 flex-shrink-0"
              onClick={onRefresh}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSync} spin={loading} />
            </Button>
          </OverlayTrigger> */}
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status" />
          <p className="mt-3 text-600 fs-10 mb-0">Loading facilities...</p>
        </div>
      ) : facilities.length === 0 ? (
        <div
          className="d-flex justify-content-center align-items-center text-center"
          style={{ height: "50px" }}
        >
          <p className="text-danger fw-bold mb-0">There is no data available in the table</p>
        </div>
      ) : (
        <>
          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle text-center"
            rowClassName="text-nowrap align-middle"
            tableProps={{
              size: "lg",
              striped: true,
              className: "fs-10 mb-0"
            }}
          />
          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </>
      )}
    </AdvanceTableProvider>
  );
};

FacilityList.propTypes = {
  facilities: PropTypes.array,
  loading: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired
};

/* ------------------------------------------------------------------ */
/* Tab: Facility settings                                              */
/* ------------------------------------------------------------------ */

const FacilityInventoryPanel = ({ facility, groupId, onSaved, embedded }) => {
  const facilityId = facility?.facility_id ?? facility?.id;

  const [facilityDetails, setFacilityDetails] = useState(null);
  const [loadingFacility, setLoadingFacility] = useState(false);
  const [savingFacility, setSavingFacility] = useState(false);
  const [facilityError, setFacilityError] = useState("");
  const [facilityInfoMessage, setFacilityInfoMessage] = useState("");

  const [inventoryEnabled, setInventoryEnabled] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [originalFacilityConfig, setOriginalFacilityConfig] = useState(null);

  // GET /inventory/config/:facilityId
  const fetchFacilityConfig = useCallback(
    async (facId) => {
      if (!facId) return;
      setLoadingFacility(true);
      setFacilityError("");
      setFacilityInfoMessage("");

      try {
        const res = await axios.get(apiUrl(`/inventory/config/${facId}`), {
          headers: authHeaders()
        });

        const config = res.data?.data || res.data?.config || res.data?.result || res.data || {};

        const isEnabled = Boolean(
          config.inventory_enabled === true ||
          config.inventory_enabled === 1 ||
          config.inventory_enabled === "true" ||
          config.inventory_enabled === "1"
        );

        const schedules = parseSchedules(
          config.inventory_schedules,
          config.inventory_schedules_list
        );

        setFacilityDetails({
          facility_id: config.facility_id || facId,
          group_id: config.group_id || groupId,
          facility_name: config.facility_name,
          is_active: config.is_active,
          inherits_from_group: Boolean(config.inherits_from_group)
        });

        setInventoryEnabled(isEnabled);
        setSelectedSchedules(schedules);
        setOriginalFacilityConfig({
          inventory_enabled: isEnabled,
          inventory_schedules: schedules
        });
      } catch (err) {
        if (err?.response?.status === 404) {
          setInventoryEnabled(false);
          setSelectedSchedules([]);
          setFacilityDetails(null);
          setOriginalFacilityConfig({
            inventory_enabled: false,
            inventory_schedules: []
          });
          setFacilityInfoMessage(
            "No existing inventory configuration was found for this facility. Default settings have been loaded. Configure and click Save Settings."
          );
        } else {
          const errorMsg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Failed to fetch facility inventory configuration.";
          setFacilityError(errorMsg);
        }
      } finally {
        setLoadingFacility(false);
      }
    },
    [groupId]
  );

  useEffect(() => {
    fetchFacilityConfig(facilityId);
  }, [fetchFacilityConfig, facilityId]);

  const toggleSchedule = (value) => {
    const val = String(value).toLowerCase();
    setSelectedSchedules((prev) =>
      prev.includes(val) ? prev.filter((item) => item !== val) : [...prev, val]
    );
  };

  const handleResetFacility = () => {
    if (originalFacilityConfig) {
      setInventoryEnabled(originalFacilityConfig.inventory_enabled);
      setSelectedSchedules([...originalFacilityConfig.inventory_schedules]);
    }
  };

  // PUT /inventory/config/:facilityId
  const handleSaveFacility = async () => {
    if (!facilityId) {
      toast.warning("Please select a facility first.");
      return;
    }

    setSavingFacility(true);
    const sortedSchedules = getSortedSchedules(selectedSchedules);

    const payload = {
      inventory_enabled: Boolean(inventoryEnabled),
      inventory_schedules: sortedSchedules.join(",")
    };

    try {
      const res = await axios.put(apiUrl(`/inventory/config/${facilityId}`), payload, {
        headers: authHeaders()
      });

      const message = res.data?.message || "Facility inventory configuration saved successfully.";
      toast.success(message);

      setOriginalFacilityConfig({
        inventory_enabled: Boolean(inventoryEnabled),
        inventory_schedules: [...selectedSchedules]
      });
      setFacilityInfoMessage("");

      if (onSaved) {
        onSaved({
          facility_id: facilityId,
          inventory_enabled: Boolean(inventoryEnabled),
          inventory_schedules: sortedSchedules.join(","),
          inventory_schedules_list: sortedSchedules
        });
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save facility inventory configuration.";
      toast.error(errorMsg);
    } finally {
      setSavingFacility(false);
    }
  };

  const hasUnsavedFacilityChanges = useMemo(() => {
    if (!originalFacilityConfig) return false;
    if (originalFacilityConfig.inventory_enabled !== inventoryEnabled) return true;
    const sortedOrig = [...originalFacilityConfig.inventory_schedules].sort().join(",");
    const sortedCurr = [...selectedSchedules].sort().join(",");
    return sortedOrig !== sortedCurr;
  }, [originalFacilityConfig, inventoryEnabled, selectedSchedules]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 mb-4 rounded-3 border border-200 bg-body-tertiary">
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faBuilding} className="text-primary" />
          <span className="fw-bold text-900">
            {facilityDetails?.facility_name || facility?.facility_name || "Facility"}
          </span>
        </div>
        {facilityDetails && (
          <SubtleBadge bg={facilityDetails.inherits_from_group ? "warning" : "primary"} pill>
            {facilityDetails.inherits_from_group
              ? "Using group defaults"
              : "Facility custom settings"}
          </SubtleBadge>
        )}
      </div>

      {loadingFacility && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status" />
          <p className="mt-3 text-600 fs-10 mb-0">Loading facility settings...</p>
        </div>
      )}

      {facilityError && !loadingFacility && (
        <Alert variant="danger" className="d-flex align-items-start border-0 rounded-3 mb-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
          <div className="flex-1">
            <div className="fw-bold fs-10">We couldn&apos;t load these settings</div>
            <div className="fs-10">{facilityError}</div>
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3 rounded-pill px-3"
            onClick={() => fetchFacilityConfig(facilityId)}
          >
            Retry
          </Button>
        </Alert>
      )}

      {facilityInfoMessage && !loadingFacility && (
        <Alert variant="info" className="d-flex align-items-start border-0 rounded-3 mb-4 fs-10">
          <FontAwesomeIcon icon={faInfoCircle} className="me-3 mt-1 fs-9" />
          <div>{facilityInfoMessage}</div>
        </Alert>
      )}

      {!loadingFacility && (
        <>
          {facilityDetails?.inherits_from_group && (
            <Alert
              variant="warning"
              className="d-flex align-items-start border-0 rounded-3 mb-4 fs-10"
            >
              <FontAwesomeIcon icon={faInfoCircle} className="me-3 mt-1 fs-9" />
              <div>
                This facility currently follows the group defaults. Saving below will give it its own
                settings.
              </div>
            </Alert>
          )}

          {/* Step 1 — enable */}
          <Card className="border border-200 shadow-none mb-4">
            <Card.Body className="p-3 p-md-4">
              <Row className="align-items-center g-3">
                <Col xs={12} sm={8}>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <SubtleBadge bg="secondary" pill className="fs-11">
                      Step 1
                    </SubtleBadge>
                    <h6 className="mb-0 fw-bold text-900 fs-10">Inventory tracking</h6>
                  </div>
                  <p className="text-600 mb-0 fs-10">
                    When on, this facility tracks medication stock and applies the schedule rules
                    below.
                  </p>
                </Col>
                <Col xs={12} sm={4} className="text-sm-end">
                  <div className="d-inline-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id={`inventory-enable-switch-${facilityId || "none"}`}
                      className="mb-0"
                      style={{ fontSize: "1.35rem" }}
                      checked={inventoryEnabled}
                      onChange={(e) => setInventoryEnabled(e.target.checked)}
                      label=""
                    />
                    <SubtleBadge bg={inventoryEnabled ? "success" : "secondary"} pill>
                      {inventoryEnabled ? "On" : "Off"}
                    </SubtleBadge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Step 2 — schedules */}
          <Card
            className={`border shadow-none mb-4 ${
              inventoryEnabled ? "border-200" : "border-200 opacity-75"
            }`}
          >
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex align-items-center gap-2 mb-3">
                <SubtleBadge bg="secondary" pill className="fs-11">
                  Step 2
                </SubtleBadge>
                <span className="fw-bold text-900 fs-10">Drug schedules</span>
              </div>

              <ScheduleGrid
                idPrefix={`schedule-${facilityId || "none"}`}
                selected={selectedSchedules}
                onToggle={toggleSchedule}
                onAll={() => setSelectedSchedules(SCHEDULE_OPTIONS.map((o) => o.value.toLowerCase()))}
                onControlled={() => setSelectedSchedules(["2", "3", "4", "5"])}
                onClear={() => setSelectedSchedules([])}
                title="Drug schedules to track"
                subtitle="Tap a card to include that schedule in inventory tracking."
                compact={embedded}
              />

              {inventoryEnabled && selectedSchedules.length === 0 && (
                <div className="text-warning fs-11 mt-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  Inventory is on but no schedules are selected — nothing will be tracked.
                </div>
              )}
            </Card.Body>
          </Card>

          <SaveBar
            sticky={!embedded}
            dirty={hasUnsavedFacilityChanges}
            saving={savingFacility}
            onSave={handleSaveFacility}
            onReset={handleResetFacility}
            saveLabel="Save settings"
          />
        </>
      )}
    </>
  );
};

FacilityInventoryPanel.propTypes = {
  facility: PropTypes.object,
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSaved: PropTypes.func,
  embedded: PropTypes.bool
};

/* Inventory settings for one facility, in a modal (used by the group profile page). */
export const FacilityInventoryModal = ({ show, facility, groupId, onHide, onSaved }) => (
  <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
    <Modal.Header closeButton className="bg-body-tertiary border-bottom border-200">
      <Modal.Title className="fs-8 fw-bold text-900">Inventory Configuration</Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-3 p-md-4">
      {show && (
        <FacilityInventoryPanel
          key={facility?.facility_id ?? facility?.id}
          facility={facility}
          groupId={groupId}
          onSaved={onSaved}
          embedded
        />
      )}
    </Modal.Body>
  </Modal>
);

FacilityInventoryModal.propTypes = {
  show: PropTypes.bool,
  facility: PropTypes.object,
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onHide: PropTypes.func,
  onSaved: PropTypes.func
};

export const FacilitySettings = ({ initialFacility }) => {
  const { groupId, groupFacilities, setGroupFacilities, loadingGroup, refresh } =
    useGroupInventory();

  const [selectedFacility, setSelectedFacility] = useState(initialFacility || null);

  const openFacility = useCallback((fac) => {
    setSelectedFacility(fac);
  }, []);

  const backToList = () => {
    setSelectedFacility(null);
  };

  const handleSaved = (saved) => {
    setGroupFacilities((prev) =>
      prev.map((f) =>
        String(f.facility_id) === String(saved.facility_id)
          ? {
              ...f,
              inventory_enabled: saved.inventory_enabled,
              inventory_schedules: saved.inventory_schedules,
              inventory_schedules_list: saved.inventory_schedules_list,
              inherits_from_group: false
            }
          : f
      )
    );
    refresh();
  };

  /* -------------------- list view -------------------- */
  if (!selectedFacility) {
    return (
      <div className="pt-3">
        <SectionHeading
          title="Inventory"
          subtitle="Turn inventory tracking on for a facility and choose the drug schedules it tracks."
        />
        <FacilityList
          facilities={groupFacilities}
          loading={loadingGroup}
          onSelect={openFacility}
          onRefresh={refresh}
        />
      </div>
    );
  }

  /* -------------------- detail view -------------------- */
  return (
    <div className="pt-3">
      <Button variant="link" className="p-0 mb-3 fs-10 text-decoration-none" onClick={backToList}>
        <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
        Back to facilities
      </Button>

      <FacilityInventoryPanel
        key={selectedFacility.facility_id}
        facility={selectedFacility}
        groupId={groupId}
        onSaved={handleSaved}
      />
    </div>
  );
};

FacilitySettings.propTypes = {
  initialFacility: PropTypes.object
};

/* ------------------------------------------------------------------ */
/* Tab: Group defaults (behaviour unchanged)                           */
/* ------------------------------------------------------------------ */

export const GroupDefaults = ({
  onConfigureFacility,
  groupId: groupIdProp,
  heading = "Inventory",
  subheading = "Applies to every facility that has no inventory settings of its own."
}) => {
  const { groupId, groupConfig, groupFacilities, loadingGroup, refresh } =
    useGroupInventory(groupIdProp);

  const [groupSchedules, setGroupSchedules] = useState([]);
  const [groupTrackAllPrn, setGroupTrackAllPrn] = useState(false);
  const [originalGroupConfig, setOriginalGroupConfig] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [auditCfg, setAuditCfg] = useState(null);
  const [originalAuditCfg, setOriginalAuditCfg] = useState(null);

  useEffect(() => {
    if (!groupConfig) return;
    const audit = readAuditConfig(groupConfig);
    setAuditCfg(audit);
    setOriginalAuditCfg(audit);
    const parsed = parseSchedules(
      groupConfig.inventory_schedules,
      groupConfig.inventory_schedules_list
    );
    const trackPrn = Boolean(groupConfig.inventory_track_all_prn);
    setGroupSchedules(parsed);
    setGroupTrackAllPrn(trackPrn);
    setOriginalGroupConfig({
      inventory_schedules: parsed,
      inventory_track_all_prn: trackPrn
    });
  }, [groupConfig]);

  const toggleGroupSchedule = (val) => {
    const s = String(val).toLowerCase();
    setGroupSchedules((prev) =>
      prev.includes(s) ? prev.filter((item) => item !== s) : [...prev, s]
    );
  };

  const auditErrors = useMemo(() => validateAuditSettings(auditCfg), [auditCfg]);

  const auditDirty = useMemo(() => {
    if (!auditCfg || !originalAuditCfg) return false;
    return ["enabled", "intervalDays", "intervalHours", "graceDays", "blocksMedpass"].some(
      (k) => String(auditCfg[k]) !== String(originalAuditCfg[k])
    );
  }, [auditCfg, originalAuditCfg]);

  // PUT /inventory/config/group/:groupId
  const handleSaveGroup = async () => {
    if (!groupId) {
      toast.warning("No group available.");
      return;
    }
    if (Object.keys(auditErrors).length > 0) {
      toast.warning("Please fix the periodic audit settings before saving.");
      return;
    }

    setSavingGroup(true);
    const sortedSchedules = getSortedSchedules(groupSchedules);
    const payload = {
      inventory_schedules: sortedSchedules.join(","),
      inventory_track_all_prn: Boolean(groupTrackAllPrn),
      // Audit fields go out only when changed, so an untouched grace value
      // (including the -999 sentinel) is never rewritten.
      ...(auditDirty ? auditConfigPayload(auditCfg) : {})
    };

    try {
      const res = await axios.put(apiUrl(`/inventory/config/group/${groupId}`), payload, {
        headers: authHeaders()
      });

      const message = res.data?.message || "Group inventory configuration saved successfully.";
      toast.success(message);

      setOriginalGroupConfig({
        inventory_schedules: [...groupSchedules],
        inventory_track_all_prn: Boolean(groupTrackAllPrn)
      });
      setOriginalAuditCfg(auditCfg);
      refresh();
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save group inventory configuration.";
      toast.error(errorMsg);
    } finally {
      setSavingGroup(false);
    }
  };

  const hasUnsavedGroupChanges = useMemo(() => {
    if (auditDirty) return true;
    if (!originalGroupConfig) return false;
    if (originalGroupConfig.inventory_track_all_prn !== groupTrackAllPrn) return true;
    const sortedOrig = [...originalGroupConfig.inventory_schedules].sort().join(",");
    const sortedCurr = [...groupSchedules].sort().join(",");
    return sortedOrig !== sortedCurr;
  }, [originalGroupConfig, groupTrackAllPrn, groupSchedules, auditDirty]);

  return (
    <div className="pt-3">
      <Card className="border border-200 shadow-none mb-4">
        {heading && (
          <Card.Header className="bg-body-tertiary border-bottom border-200 py-3">
            <h6 className="mb-0 fw-bold text-900 fs-9">{heading}</h6>
            {subheading && <p className="text-600 mb-0 fs-10 mt-1">{subheading}</p>}
          </Card.Header>
        )}
        <Card.Body className="p-3 p-md-4">
          <ScheduleGrid
            idPrefix="group-schedule"
            selected={groupSchedules}
            onToggle={toggleGroupSchedule}
            onAll={() => setGroupSchedules(SCHEDULE_OPTIONS.map((o) => o.value.toLowerCase()))}
            onControlled={() => setGroupSchedules(["2", "3", "4", "5"])}
            onClear={() => setGroupSchedules([])}
            title="Default drug schedules"
            subtitle="Facilities without their own settings will track these. PRN covers as-needed orders on any schedule."
            prn={{ checked: groupTrackAllPrn, onToggle: setGroupTrackAllPrn }}
          />

          <AuditSettings value={auditCfg} onChange={setAuditCfg} errors={auditErrors} />

          <div className="mt-4">
            <SaveBar
              dirty={hasUnsavedGroupChanges}
              saving={savingGroup}
              onSave={handleSaveGroup}
              saveLabel="Save group defaults"
            />
          </div>
        </Card.Body>
      </Card>

      {/* Facilities overview */}
      {/* <Card className="border border-200 shadow-none overflow-hidden">
        <Card.Header className="bg-body-tertiary border-bottom border-200 py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h6 className="mb-1 fw-bold text-900 fs-10">
              <FontAwesomeIcon icon={faList} className="text-primary me-2" />
              All facilities ({groupFacilities.length})
            </h6>
            <p className="text-600 mb-0 fs-10">
              Current inventory settings across every facility in this group.
            </p>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="rounded-pill px-3"
            onClick={refresh}
            disabled={loadingGroup}
          >
            <FontAwesomeIcon icon={faSync} spin={loadingGroup} className="me-1" />
            Refresh
          </Button>
        </Card.Header>

        <Card.Body className="p-0">
          {loadingGroup ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" role="status" />
              <p className="mt-3 text-600 fs-10 mb-0">Loading facilities...</p>
            </div>
          ) : groupFacilities.length === 0 ? (
            <div className="text-center py-5 text-600 fs-10">
              No facilities found for this group.
            </div>
          ) : (
            <Table responsive hover className="mb-0 fs-10 align-middle">
              <thead>
                <tr className="bg-body-tertiary text-700">
                  <th className="border-0 ps-3 text-uppercase fs-11 fw-semibold">Facility Name</th>
                  <th className="border-0 text-uppercase fs-11 fw-semibold">Inventory Status</th>
                  <th className="border-0 text-uppercase fs-11 fw-semibold">Tracked Schedules</th>
                  <th className="border-0 text-uppercase fs-11 fw-semibold">Configuration Mode</th>
                  <th className="border-0 pe-3 text-end text-uppercase fs-11 fw-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {groupFacilities.map((fac) => {
                  const schedules = parseSchedules(
                    fac.inventory_schedules,
                    fac.inventory_schedules_list
                  );
                  return (
                    <tr key={fac.facility_id}>
                      <td className="ps-3 fw-semibold text-900">
                        {fac.facility_name || "Facility"}
                      </td>
                      <td>
                        <SubtleBadge
                          bg={fac.inventory_enabled ? "success" : "secondary"}
                          pill
                          className="d-inline-block"
                        >
                          <FontAwesomeIcon
                            icon={fac.inventory_enabled ? faCheckCircle : faInfoCircle}
                            className="me-1"
                          />
                          {fac.inventory_enabled ? "Enabled" : "Disabled"}
                        </SubtleBadge>
                      </td>
                      <td style={{ minWidth: 190 }}>
                        <ScheduleChips schedules={schedules} />
                      </td>
                      <td>
                        <SubtleBadge
                          bg={fac.inherits_from_group ? "warning" : "primary"}
                          pill
                          className="d-inline-block"
                        >
                          {fac.inherits_from_group ? "Group default" : "Facility custom"}
                        </SubtleBadge>
                      </td>
                      <td className="pe-3 text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="py-1 px-3 fs-11 rounded-pill"
                          onClick={() => onConfigureFacility && onConfigureFacility(fac)}
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-1" />
                          Configure
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card> */}
    </div>
  );
};

GroupDefaults.propTypes = {
  onConfigureFacility: PropTypes.func,
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  heading: PropTypes.string,
  subheading: PropTypes.string
};

/* ------------------------------------------------------------------ */
/* Default export — renders one of the two sections                    */
/* ------------------------------------------------------------------ */

const InventoryConfig = ({ section = "facility", ...rest }) =>
  section === "group" ? <GroupDefaults {...rest} /> : <FacilitySettings {...rest} />;

InventoryConfig.propTypes = {
  section: PropTypes.oneOf(["facility", "group"])
};

export default InventoryConfig;
